/** @format */

// Generic, provider-agnostic image helpers for upload → crop → resize →
// compress flows. Nothing in here knows about providers, patients or any API;
// any image-upload screen can reuse these as-is.

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

export const DEFAULT_SQUARE_OPTIONS = {
  size: 400,
  maxBytes: 100 * 1024,
  mimeType: "image/jpeg",
  startQuality: 0.92,
  minQuality: 0.4,
  qualityStep: 0.08,
};

// Actual encoded byte size of a data URL (base64 portion only).
export const base64ToByteLength = (dataUrl = "") => {
  const base64 = (dataUrl.split(",")[1] || "").replace(/\s/g, "");
  return Math.ceil((base64.length * 3) / 4);
};

export const base64ToBlob = (dataUrl = "", mimeType = "image/jpeg") => {
  const base64 = (dataUrl.split(",")[1] || "").replace(/\s/g, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

export const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not read image"));
    image.src = src;
  });
};

// Extracts the user-selected rectangle (react-easy-crop `croppedAreaPixels`)
// at its natural resolution — no resizing, no re-encoding to a smaller size.
// The caller decides what to do next (typically compressImageToSquare).
// Resolves with a Blob so the result can feed straight into compression.
export const getCroppedImage = (
  imageSrc,
  croppedAreaPixels,
  { mimeType = "image/jpeg", quality = 0.95 } = {},
) => {
  return new Promise((resolve, reject) => {
    if (!imageSrc || !croppedAreaPixels) {
      reject(new Error("Missing image or crop area"));
      return;
    }
    loadImage(imageSrc).then(
      (image) => {
        try {
          const { width, height, x, y } = croppedAreaPixels;
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(width);
          canvas.height = Math.round(height);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(
            image,
            x,
            y,
            width,
            height,
            0,
            0,
            canvas.width,
            canvas.height,
          );
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Could not crop image"));
            },
            mimeType,
            quality,
          );
        } catch (err) {
          reject(err);
        }
      },
      (err) => reject(err),
    );
  });
};

// Resizes a (normally already 1:1 cropped) image source to exactly
// `size x size` JPEG and steps the JPEG quality down until the output fits
// under `maxBytes`. Dimensions are never reduced below `size x size` — only
// the encoding quality is lowered. If a non-square source is passed (e.g. a
// caller skipped the crop step), it is center-cropped to a square first so
// the result is still a real 1:1 image and never stretched/distorted.
// Accepts a File or Blob; resolves with { dataUrl, bytes }.
export const compressImageToSquare = (
  source,
  {
    size = DEFAULT_SQUARE_OPTIONS.size,
    maxBytes = DEFAULT_SQUARE_OPTIONS.maxBytes,
    mimeType = DEFAULT_SQUARE_OPTIONS.mimeType,
    startQuality = DEFAULT_SQUARE_OPTIONS.startQuality,
    minQuality = DEFAULT_SQUARE_OPTIONS.minQuality,
    qualityStep = DEFAULT_SQUARE_OPTIONS.qualityStep,
  } = {},
) => {
  return new Promise((resolve, reject) => {
    if (!source) {
      reject(new Error("Missing image source"));
      return;
    }
    let objectUrl = null;
    try {
      objectUrl =
        typeof source === "string" ? source : URL.createObjectURL(source);
    } catch (err) {
      reject(err);
      return;
    }
    const revoke = () => {
      if (objectUrl && typeof source !== "string") {
        URL.revokeObjectURL(objectUrl);
      }
    };

    loadImage(objectUrl).then(
      (image) => {
        revoke();
        try {
          // Safety net: the crop modal already guarantees a 1:1 source, but
          // a square center-crop here keeps the output undistorted even if a
          // future caller passes an uncropped portrait/landscape image.
          const side = Math.min(image.naturalWidth, image.naturalHeight);
          const sx = (image.naturalWidth - side) / 2;
          const sy = (image.naturalHeight - side) / 2;

          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(image, sx, sy, side, side, 0, 0, size, size);

          let quality = startQuality;
          let dataUrl = canvas.toDataURL(mimeType, quality);

          while (
            base64ToByteLength(dataUrl) > maxBytes &&
            quality > minQuality
          ) {
            quality = Math.max(minQuality, quality - qualityStep);
            const next = canvas.toDataURL(mimeType, quality);
            if (next === dataUrl) break; // quality floor reached
            dataUrl = next;
          }

          resolve({ dataUrl, bytes: base64ToByteLength(dataUrl) });
        } catch (err) {
          reject(err);
        }
      },
      (err) => {
        revoke();
        reject(err);
      },
    );
  });
};
